"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateChargeMasterSchema = exports.createChargeMasterSchema = exports.chargeMasterShape = void 0;
/**
 * Enterprise Charge Master, Phase 1 (see
 * `docs/ENTERPRISE-CHARGE-MASTER-ARCHITECTURE.md`).
 *
 * `sacCode`/`hsnCode`/`revenueCenterId`/`costCenterId`/`effectiveFrom`/
 * `effectiveTo` are Stage-5A columns that have existed on `mst_charges`
 * since ADR-018 but were never added to this shape — and because
 * `validateRequest` assigns `req.body = result.data`, Zod silently stripped
 * them from every request, leaving six real database columns permanently
 * unreachable through the API (found by the ECM pre-build audit — see
 * `ENTERPRISE-CHARGE-MASTER-AUDIT.md` §5). Exposing them here is a contract
 * fix with **no schema change and no migration** — the columns already
 * exist — which is why it belongs to Phase 1 rather than Phase 2's catalog
 * extension.
 *
 * Revenue-centre/cost-centre mapping in particular is load-bearing: the ECM
 * brief lists "No missing finance mapping" as a hard validation, and it
 * could not even be entered before this.
 */
const zod_1 = require("zod");
exports.chargeMasterShape = {
    /**
     * ECM Phase 2 — now OPTIONAL on create: omitting it auto-generates the next
     * code from the tenant's `CHARGE_MASTER` sequence (`CHG000001`), per the
     * brief's "Auto Generated / Configurable prefix". An explicitly supplied
     * code is still honoured, which is what makes bulk-importing a hospital's
     * existing coded catalog possible.
     */
    chargeCode: zod_1.z.string().min(1).max(30).optional(),
    name: zod_1.z.string().min(1).max(255),
    categoryId: zod_1.z.string().uuid().optional(),
    departmentId: zod_1.z.string().uuid().optional(),
    taxTypeId: zod_1.z.string().uuid().optional(),
    // Stage-5A columns — real since ADR-018, unreachable through the API until Phase 1.
    sacCode: zod_1.z.string().max(10).optional(),
    hsnCode: zod_1.z.string().max(10).optional(),
    revenueCenterId: zod_1.z.string().uuid().optional(),
    costCenterId: zod_1.z.string().uuid().optional(),
    /**
     * ADR-081 Phase 3 — the ACCOUNTING bridge.
     *
     * Distinct from `revenueCenterId` above, which is a REPORTING dimension and
     * has never resolved a ledger (D-5). Adding these to the shape is not
     * cosmetic: a field absent from the schema is silently STRIPPED by Zod, so
     * the form would appear to save and the column would stay null — the exact
     * defect the Enterprise Charge Master phase already hit with six columns.
     */
    postingGroupId: zod_1.z.string().uuid().nullish(),
    /** The escape hatch: wins over the posting group's mapping (D-3 step 1). */
    revenueAccountOverrideId: zod_1.z.string().uuid().nullish(),
    effectiveFrom: zod_1.z.coerce.date().optional(),
    effectiveTo: zod_1.z.coerce.date().optional(),
    // ECM Phase 2 — catalog extension (see the architecture doc §3.1).
    displayName: zod_1.z.string().max(255).optional(),
    shortName: zod_1.z.string().max(60).optional(),
    description: zod_1.z.string().optional(),
    chargeTypeId: zod_1.z.string().uuid().optional(),
    subCategoryId: zod_1.z.string().uuid().optional(),
    serviceGroupId: zod_1.z.string().uuid().optional(),
    internalCode: zod_1.z.string().max(50).optional(),
    hisCode: zod_1.z.string().max(50).optional(),
    lisCode: zod_1.z.string().max(50).optional(),
    risCode: zod_1.z.string().max(50).optional(),
    erpCode: zod_1.z.string().max(50).optional(),
    accountingCode: zod_1.z.string().max(50).optional(),
    barcode: zod_1.z.string().max(100).optional(),
    isBillable: zod_1.z.boolean().optional(),
};
/** Shared by create/update — an open effective window (no `effectiveTo`) is valid. */
function hasValidEffectiveWindow(value) {
    if (!value.effectiveFrom || !value.effectiveTo)
        return true;
    return value.effectiveTo >= value.effectiveFrom;
}
const EFFECTIVE_WINDOW_ERROR = {
    message: 'effectiveTo must be on or after effectiveFrom',
    path: ['effectiveTo'],
};
exports.createChargeMasterSchema = zod_1.z.object(exports.chargeMasterShape).refine(hasValidEffectiveWindow, EFFECTIVE_WINDOW_ERROR);
/**
 * Built from the raw shape rather than `createChargeMasterSchema.partial()` —
 * `.refine()` returns a `ZodEffects`, which has no `.partial()`. PATCH still
 * enforces the same window rule whenever both dates are supplied together.
 */
exports.updateChargeMasterSchema = zod_1.z.object(exports.chargeMasterShape).partial().refine(hasValidEffectiveWindow, EFFECTIVE_WINDOW_ERROR);
//# sourceMappingURL=charge-master.schema.js.map